import React, { useCallback } from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { Inline, Text } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';

import type { FilterConfig } from '../../types';
import { messages } from '../messages';

interface SelectedFiltersRowProps {
	filterConfig: FilterConfig;
	selectedFilters: string[];
	onFilterChange: (filters: string[]) => void;
}

/**
 * Component to display the selected filters
 */
export function SelectedFiltersRow({
	filterConfig,
	selectedFilters,
	onFilterChange,
}: SelectedFiltersRowProps) {
	const { formatMessage } = useIntl();
	const { filters } = filterConfig;
	const removeFilter = useCallback(
		(key: string) => {
			const newFilters = selectedFilters.filter((filter) => filter !== key);
			onFilterChange(newFilters);
			// return truthy value to remove tag
			return true;
		},
		[onFilterChange, selectedFilters],
	);

	return (
		<Inline space="space.050" alignBlock="center">
			{/* aria-hidden is not supported by Text primitive, and ratcheting rules don't allow us to use a pure <span> */}
			<div aria-hidden="true">
				<Text xcss={styles.filterSelectedLabel}>
					{formatMessage(messages.selectedFiltersVisualLabel)}
				</Text>
			</div>
			<TagGroup label={formatMessage(messages.selectedFiltersAccessibleLabel)}>
				{selectedFilters.map((filterKey) => {
					const filterLabel = filters?.find((filterItem) => filterItem.key === filterKey)?.label;
					return filterLabel ? (
						<Tag
							key={filterKey}
							text={filterLabel}
							removeButtonLabel={formatMessage(messages.removeFilterIconLabel)}
							onBeforeRemoveAction={() => removeFilter(filterKey)}
						/>
					) : null;
				})}
			</TagGroup>
		</Inline>
	);
}

const styles = cssMap({
	filterSelectedLabel: {
		overflowWrap: 'normal',
	},
});
